import React, { useState } from 'react';
import type {
  DefaultValues,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import {
  Box,
  Checkbox,
  type DialogProps,
  FormControlLabel,
  Portal,
} from '@mui/material';
import Form from '../forms/Form';
import ActionButton from '../ui/ActionButton';
import Dialog from './Dialog';

export interface FormDialogProps<T extends FieldValues> {
  open: boolean;
  onClose: () => void;
  title: string;
  titleAction?:
    | React.ReactNode
    | ((methods: UseFormReturn<T>) => React.ReactNode);
  defaultValues: DefaultValues<T>;
  onSubmit: (data: T, methods: UseFormReturn<T>) => Promise<void>;
  submitText?: string;
  cancelText?: string;
  maxWidth?: DialogProps['maxWidth'];
  /** Adds a checkbox that, when checked, resets the form and keeps the dialog open after submit. */
  addAnother?: boolean;
  /** When set, shows a left-side "Reset form" button that restores these values. */
  resetValues?: DefaultValues<T>;
  /** Called before the form is reset when Reset form is clicked. */
  onReset?: () => void;
  children: React.ReactNode | ((methods: UseFormReturn<T>) => React.ReactNode);
}

const FormDialog = <T extends FieldValues>({
  open,
  onClose,
  title,
  titleAction,
  defaultValues,
  onSubmit,
  submitText = 'Save',
  cancelText = 'Cancel',
  maxWidth = 'sm',
  addAnother = false,
  resetValues,
  onReset,
  children,
}: FormDialogProps<T>) => {
  const [keepOpen, setKeepOpen] = useState(false);
  const showReset = resetValues != null;

  const handleClose = () => {
    onClose();
  };

  const handleFormSubmit = async (data: T, methods: UseFormReturn<T>) => {
    await onSubmit(data, methods);
    if (addAnother && keepOpen) {
      methods.reset(defaultValues);
      const firstField = Object.keys(defaultValues)[0] as
        | FieldPath<T>
        | undefined;
      if (firstField) {
        methods.setFocus(firstField);
      }
      return;
    }
    handleClose();
  };

  if (!open) {
    return null;
  }

  return (
    <Portal>
      <Form<T>
        defaultValues={defaultValues}
        onSubmit={handleFormSubmit}
        resetOn={open}
      >
        {(methods) => {
          const actionsStart =
            showReset || addAnother ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {showReset ? (
                  <ActionButton
                    variant="ghost"
                    onClick={() => {
                      onReset?.();
                      methods.reset(resetValues);
                    }}
                    disabled={methods.formState.isSubmitting}
                  >
                    Reset form
                  </ActionButton>
                ) : null}
                {addAnother ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={keepOpen}
                        onChange={(event) => setKeepOpen(event.target.checked)}
                      />
                    }
                    label="Keep adding"
                    slotProps={{
                      typography: {
                        variant: 'body2',
                        color: 'text.secondary',
                      },
                    }}
                  />
                ) : null}
              </Box>
            ) : undefined;

          return (
            <Dialog
              open={open}
              onClose={handleClose}
              title={title}
              titleAction={
                typeof titleAction === 'function'
                  ? titleAction(methods)
                  : titleAction
              }
              disablePortal
              isSubmitting={methods.formState.isSubmitting}
              submitText={submitText}
              cancelText={cancelText}
              maxWidth={maxWidth}
              actionsStart={actionsStart}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {typeof children === 'function' ? children(methods) : children}
              </Box>
            </Dialog>
          );
        }}
      </Form>
    </Portal>
  );
};

export default FormDialog;
